import React, { useState, useEffect } from 'react'
import { validationColors } from '../assets/data';
import { useChangePasswordMutation, useGetUserByIdQuery } from '../features/users/usersApiSlice';
import InputBox from './InputBox';
import { MdVisibility } from "react-icons/md";
import { AiFillEyeInvisible } from "react-icons/ai";
import { IoCloseOutline } from "react-icons/io5";
import { MdModeEdit } from "react-icons/md";

import { Button } from "./ui/button";
import useAuth from '../hooks/useAuth';

const PWD_INPUT_VALIDATOR_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@#$%^&+=!]).{8,16}$/;
const OLD_PWD_LENGTH_VALIDATOR_REGEX = /^.{6,20}$/;

const ChangePassword = () => {
  const { id } = useAuth();
  const { data: user } = useGetUserByIdQuery(id);
  const [updatePassword, { isSuccess }] = useChangePasswordMutation();

  const [edit, setEdit] = useState(false);

  const [oldPassword, setOldPassword] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [validOldPassword, setValidOldPassword] = useState(false);
  const [validPassword, setValidPassword] = useState(false);

  const [hasCapitalLetter, setHasCapitalLetter] = useState(false);
  const [hasSpecialCharacter, setHasSpecialCharacter] = useState(false);
  const [hasNumber, setHasNumber] = useState(false);
  const [hasValidLength, setHasValidLength] = useState(false);

  const [validConfirmPassword, setValidConfirmPassword] = useState(false);

  useEffect(() => { setValidOldPassword(OLD_PWD_LENGTH_VALIDATOR_REGEX.test(oldPassword)); }, [oldPassword]);
  useEffect(() => { setValidPassword(PWD_INPUT_VALIDATOR_REGEX.test(password)); }, [password]);
  useEffect(() => { setValidConfirmPassword(password === confirmPassword); }, [password, confirmPassword]);

  const onOldPasswordChanged = (e) => setOldPassword(e.target.value);
  const onPasswordChanged = (e) => setPassword(e.target.value);
  const onConfirmPasswordChanged = (e) => setConfirmPassword(e.target.value);

  useEffect(() => {
    setHasCapitalLetter(/[A-Z]/.test(password));
    setHasSpecialCharacter(/[@#$%^&+=!]/.test(password));
    setHasNumber(/\d/.test(password));
    setHasValidLength(password.length >= 8 && password.length <= 16);
  }, [password]);

  const canSave =
    [
      validOldPassword,
      validPassword,
      validConfirmPassword,
    ].every(Boolean);

  const onUpdatePasswordClicked = async (e) => {
    e.preventDefault();
    if (canSave) {
      await updatePassword({id, oldPassword, password});
    }
  }
  useEffect(() => {
    if (isSuccess) {
      setEdit(false);
    }
  }, [isSuccess]);

  const validOldPwdClass = !validOldPassword && oldPassword !== "" ? validationColors.bgInvalid : "";
  const validPwdClass = !validPassword && password !== "" ? validationColors.bgInvalid : "";
  const confirmPwdDisable = validPassword ? false : true;
  const validConfirmPwdClass = !confirmPwdDisable ? !validConfirmPassword ? validationColors.bgInvalid : "" : "";

  let [showOP, setShowOP] = useState(false);
  let [showP, setShowP] = useState(false);
  let [showCP, setShowCP] = useState(false);
  
  let content;
  if(user) {
    content = (
      edit === true ? (
      <form 
        className='w-full max-w-2xl flex flex-col justify-start items-start gap-4 bg-white px-7 py-10 rounded-md shadow-md relative border border-slate-200'
        onSubmit={onUpdatePasswordClicked}
      >
        <div className="w-full flex justify-between items-center mb-2">
          <p className='font-semibold text-[1.3rem] text-slate-800'>Change Password</p>
        </div>
        <span 
          className='text-[1.3rem] cursor-pointer text-slate-500/90 absolute top-3 right-3 hover:text-slate-800 transition-colors' 
          title='Cancel Edit' 
          onClick={() => {setEdit(false)}}
        >
          <IoCloseOutline />
        </span>

        <div className="w-full space-y-4">
          <InputBox 
            usedFor="oldPassword" 
            boxLabel="Current Password" 
            value={oldPassword} 
            login={true} 
            onChange={onOldPasswordChanged} 
            validationClass={validOldPwdClass} 
            typeChange={1} 
            showPassword={showOP} 
            mustFill={true}
            className="w-full"
          >
            <div
              className="text-neutral-500 text-[1.3rem] absolute top-0 right-2 h-full flex justify-end items-center cursor-pointer border-0 bg-transparent"
              onClick={() => setShowOP(!showOP)}
            >
              {showOP ? <MdVisibility /> : <AiFillEyeInvisible />}
            </div>
          </InputBox>

          <div className='w-full border-t border-slate-200 my-2'></div>

          <InputBox 
            usedFor="password" 
            boxLabel="New Password" 
            value={password} 
            login={true} 
            onChange={onPasswordChanged} 
            validationClass={validPwdClass} 
            typeChange={1} 
            showPassword={showP} 
            mustFill={true}
            className="w-full"
          >
            <div
              className="text-neutral-500 text-[1.3rem] absolute top-0 right-2 h-full flex justify-end items-center cursor-pointer border-0 bg-transparent"
              onClick={() => setShowP(!showP)}
            >
              {showP ? <MdVisibility /> : <AiFillEyeInvisible />}
            </div>
          </InputBox>

          <div className={`${!validPwdClass ? "hidden" : "flex"} flex-col justify-start items-start w-full pl-2 *:leading-4 bg-slate-50 p-3 rounded-lg border border-slate-200`}>
            <h2 className="text-slate-700 font-medium mb-2">Password Requirements:</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <p className={`text-sm ${hasCapitalLetter ? 'text-green-500 font-medium' : 'text-slate-500'}`}>
                <span className="material-icons text-sm mr-1 align-text-bottom">
                  {hasCapitalLetter ? 'check_circle' : 'radio_button_unchecked'}
                </span>
                Capital letter
              </p>
              <p className={`text-sm ${hasSpecialCharacter ? 'text-green-500 font-medium' : 'text-slate-500'}`}>
                <span className="material-icons text-sm mr-1 align-text-bottom">
                  {hasSpecialCharacter ? 'check_circle' : 'radio_button_unchecked'}
                </span>
                Special character
              </p>
              <p className={`text-sm ${hasNumber ? 'text-green-500 font-medium' : 'text-slate-500'}`}>
                <span className="material-icons text-sm mr-1 align-text-bottom">
                  {hasNumber ? 'check_circle' : 'radio_button_unchecked'}
                </span>
                Number
              </p>
              <p className={`text-sm ${hasValidLength ? 'text-green-500 font-medium' : 'text-slate-500'}`}>
                <span className="material-icons text-sm mr-1 align-text-bottom">
                  {hasValidLength ? 'check_circle' : 'radio_button_unchecked'}
                </span>
                8-16 characters
              </p>
            </div>
          </div>

          <InputBox 
            usedFor="cPassword" 
            boxLabel={`${confirmPwdDisable ? "Fill Password First" : "Confirm New Password"}`} 
            value={confirmPassword} 
            login={true} 
            onChange={onConfirmPasswordChanged} 
            validationClass={validConfirmPwdClass} 
            typeChange={1} 
            showPassword={showCP} 
            confirmPwdDisable={confirmPwdDisable} 
            mustFill={true}
            className="w-full"
          >
            <div
              className={`text-neutral-500 text-[1.3rem] absolute top-0 right-2 h-full ${confirmPwdDisable ? "hidden" : "flex"} justify-end items-center cursor-pointer border-0 bg-transparent"`}
              onClick={() => setShowCP(!showCP)}
            >
              {showCP ? <MdVisibility /> : <AiFillEyeInvisible />}
            </div>
          </InputBox>
        </div>

        <Button 
          variant="outline" 
          disabled={!canSave} 
          className="w-full text-white hover:text-white bg-gradient-to-r from-blue-500 to-blue-600 h-[50px] text-[1.2rem] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
        >
          Update Password
        </Button>
      </form>
      ): (
        <div className='flex flex-col justify-start items-start rounded-md py-7 px-10 shadow-md bg-white border border-slate-200 select-none relative w-full max-w-2xl'>
          <span
            className="absolute top-3 right-3 m-auto rounded-full text-slate-500/90 hover:text-black cursor-pointer"
            title="Edit information"
            onClick={() => {setEdit(true)}}
          >
            <MdModeEdit />
          </span>

          <p className='font-semibold text-[1.3rem]'>Password Settings</p>
          <p className='text-[.8rem] text-slate-500 pl-1 leading-4'>Last changed: {new Date(user.updatedAt).toLocaleDateString()}</p>
          <div className='mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg'>
            <p className='text-blue-700 text-sm'>
              Keep your account secure by regularly updating your password. 
              Make sure to use a strong password that includes a mix of letters, numbers, and special characters.
            </p>
          </div>
        </div>
      )
    );
  }

  return content;
}

export default ChangePassword;